import React, { useEffect, useState } from 'react';
import usePrevious from 'hooks/usePrevious';
import * as R from 'ramda';
import { Dimension, Product, ProductType } from '@inter-ikea-storageone/range';
import { Entity, Nullable } from '@inter-ikea-core/core';
import { useCore } from 'contexts/core';
import { SelectedProduct } from 'ducks/products';
import { FilterOption, getUnit } from 'filters/SizeFilter/utils';
import { getDoorsFromSelectedEntity, getReplaceableDoors } from '../../utils/changeSize/doorUtils';
import { getProductFromEntity } from 'rangeConfig/shared/utils/getProductFromEntity';
import SizeToggle from 'ui/SizeToggle/SizeToggle';

const Depth = ({
  title,
  products,
  selectedProduct,
  selectedEntity,
  onSizeChange,
  framesWithSameHeight,
  availableDepths
}: {
  title?: string;
  fluidWidth?: boolean;
  products: Product[];
  selectedProduct: SelectedProduct | null;
  selectedEntity: Nullable<Entity>;
  onSizeChange: (
    size: number,
    setFilterState: any,
    framesMatchingSelectedFrame: any,
    replaceableDoors: any,
    matchFrameBy: Dimension,
    dimensionType: Dimension
  ) => void;
  framesWithSameHeight: Product[];
  availableDepths: FilterOption;
}) => {
  const [filterState, setFilterState] = useState({ selected: 0 });

  const coreApi = useCore();
  const previousDoors = getDoorsFromSelectedEntity(coreApi, selectedEntity, products);
  const [currentReplaceableDoors, setCurrentReplaceableDoors] = useState<
    Array<{ doors: Product[] }>
  >([]);
  const prevReplaceableDoors = usePrevious(currentReplaceableDoors);

  // Get the current markets unit
  const unit = getUnit(products);

  useEffect(() => {
    if (!selectedEntity) return;
    const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(
      selectedEntity,
      [ProductType.PAX_SLIDING_DOOR_REGULAR_ASM, ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM]
    );

    if (slidingDoor) {
      const slidingDoorProduct = getProductFromEntity(slidingDoor, products, coreApi);
      if (!slidingDoorProduct) return;

      const replaceableSlidingDoors = getReplaceableDoors([slidingDoorProduct]);

      if (!R.equals(prevReplaceableDoors, replaceableSlidingDoors)) {
        setCurrentReplaceableDoors(replaceableSlidingDoors);
      }
    } else {
      if (!previousDoors) return;

      const replaceableDoors = getReplaceableDoors(previousDoors);

      if (!R.equals(prevReplaceableDoors, replaceableDoors)) {
        setCurrentReplaceableDoors(replaceableDoors);
      }
    }
  }, [coreApi, prevReplaceableDoors, previousDoors, products, selectedEntity]);

  // Set the checked depth to current selected products depth
  useEffect(() => {
    if (selectedProduct) {
      setFilterState({ selected: selectedProduct?.communicatedDimensions.depth });
    }
  }, [selectedProduct, selectedProduct?.communicatedDimensions]);

  if (!selectedProduct) return null;

  const sizes = Object.entries(availableDepths).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });
  const currentSize = filterState.selected;

  return sizes.length > 0 ? (
    <SizeToggle
      title={title}
      disabled={sizes.length === 1}
      sizes={sizes}
      data-testid="change-depth"
      currentSize={currentSize}
      onChangeSize={size => {
        if (size !== currentSize) {
          onSizeChange(
            size,
            setFilterState,
            framesWithSameHeight,
            currentReplaceableDoors,
            'height',
            'depth'
          );
        }
      }}
    />
  ) : (
    <></>
  );
};

export default Depth;
